import Icon from "../Icon/Icon";
import styles from "./button.css";
import { Variants } from "./Variants.jsx";
import { useNavigate } from "react-router";

/**
 * A Button represents a fundamental UI element that allows the user to interact
 * with the app and deliberately execute commands. It communicates the action a
 * user can take. Use `OldInputSubmit` in cases of a steps that needs to be
 * submitted.
 *
 * @param {Object} props - The properties object.
 * @param {Variants} [props.variant=Variants.PRIMARY] - Determines the visual style of the button.
 * Other options: Variants.SECONDARY, Variants.TERTIARY, Variants.QUATERNARY and Variants.SMALL;
 * Variants are used through the Variants-Object which is used as an enum
 * @param {boolean} [props.disabled=false] - If true, the button will be disabled and non-interactive.
 * @param {function} [props.onClick] - Callback function to handle click events.
 * @param {string|null} [props.value=null] - The value attribute of the button.
 * @param {string} [props.width="full"] - Determines the width of the button.
 * @param {string} [props.icon] - The name of the icon to display inside the button.
 * @param {string} [props.type="button"] - The type attribute of the button element.
 * @param {React.ReactNode} props.children - The child elements to be displayed inside the button.
 * @return {JSX.Element} The rendered button element.
 */

function Button({
  variant = Variants.PRIMARY,
  disabled = false,
  onClick,
  to,
  value = null,
  width = "full",
  icon,
  type = "button",
  children,
}) {
  const navigate = useNavigate();
  function handleClick(e) {
    if (!onClick && !to) return;
    if (!onClick && to) {
      e.preventDefault();
      navigate(to);
    } else if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  }

  return (
    <button
      type={type}
      value={value}
      className={styles.button({ variant, width })}
      onClick={handleClick}
      disabled={disabled}
      title={value}
    >
      {icon && <Icon name={icon} size="20px" />}

      {children}
    </button>
  );
}

export default Button;
